<template>
  <div class="scratchcards-page pt-0">
    <NuxtLayout name="teasers" v-bind="{ loading }" :no-results="!teasers.length">
      <component
        :is="isFreeToPlayTeaser(teaser) ? FreeToPlayTeaser : ScratchcardTeaser"
        v-for="(teaser, index) in teasers"
        :key="isFreeToPlayTeaser(teaser) ? 'free-to-play-scratchcard' : teaser.slug"
        v-bind="teaser"
        :fetchpriority="index < priorityFetchAmount ? 'high' : 'low'"
        :free-to-play="isFreeToPlayTeaser(teaser)"
        @click="handleTeaserClick(teaser)"
      />

      <template #no-results>
        <LazyNoResults
          :headline="$t('home.tab.scratch.noresults')"
          illustration="sold-out"
          variant="outlined"
        />
      </template>
    </NuxtLayout>

    <HighFrequencyGameExploreMoreCarousel :variants="['for-you', 'draw', 'keno']" />
  </div>
</template>

<script lang="ts" setup>
import type {
  FreeToPlayTeaserPayload,
  ScratchcardTeaserPayload,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

import FreeToPlayTeaser from '#free-to-play/app/components/FreeToPlayTeaser.vue'
import { FREE_TO_PLAY_ITEM_NAME } from '#free-to-play/app/constants/constants'
import ScratchcardTeaser from '#scratchcard/app/components/ScratchcardTeaser.vue'

const { isDesktop } = useDevice()

const { filteredTeasers, loading } = useScratchcardFilter({ eager: true })

const priorityFetchAmount = computed(() => (isDesktop ? 6 : 3))

const TRACKING_LIST_NAME = 'Home Scratchcard Teasers'
const TRACKING_LIST_ID = 'home_scratchcard_teasers'

const { createTeaserListItems } = useTrackingData()
const { pushSelectListItemEvent, pushViewListEvent } = useTracking()
const { logTrackingError } = useTrackingErrorLog()

const trackingTeaserListItems = ref<ReturnType<typeof createTeaserListItems>>([])

const { isFreeToPlayTeaser } = useFreeToPlay()

const teasers = computed(
  () => filteredTeasers.value as (FreeToPlayTeaserPayload | ScratchcardTeaserPayload)[],
)

const handleTeaserClick = (teaser: FreeToPlayTeaserPayload | ScratchcardTeaserPayload) => {
  const teaserItem = trackingTeaserListItems.value.find((item) => {
    switch (teaser.__typename) {
      case 'FreeToPlayTeaserPayload':
        return item.item_name === FREE_TO_PLAY_ITEM_NAME
      case 'ScratchcardTeaserPayload':
        return item.item_name === teaser.name
      default:
        return false
    }
  })

  if (!teaserItem) return

  try {
    pushSelectListItemEvent({
      items: [teaserItem],
      list_id: TRACKING_LIST_ID,
      list_name: TRACKING_LIST_NAME,
    })
  } catch (err) {
    logTrackingError('select_list_item', err)
  }
}

let hasTracked = false
let unwatchTeasers: Maybe<() => void> = null
unwatchTeasers = watchImmediate(
  () => teasers.value,
  (list) => {
    if (!list || list.length === 0) return

    if (!hasTracked) {
      hasTracked = true

      try {
        trackingTeaserListItems.value = createTeaserListItems(list)
        pushViewListEvent({
          items: trackingTeaserListItems.value,
          list_id: TRACKING_LIST_ID,
          list_name: TRACKING_LIST_NAME,
        })
      } catch (err) {
        logTrackingError('view_list', err)
      }
    }

    if (unwatchTeasers) unwatchTeasers()
  },
)
</script>

<style lang="scss" scoped>
.scratchcards-page :deep(.teasers) {
  padding-bottom: token('size/space/gap/200');
}
</style>
